import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Box,
  Grid,
  TextField,
  CircularProgress,
  Alert,
  Typography,
  Paper,
} from "@mui/material";
import { Save, Map } from "@mui/icons-material";
import { ComponentHeader, BrandButton } from "components";
import {
  getContactSettings,
  updateContactSettings,
} from "actions/contactSettings";
import { clearError } from "features/contactSettingsSlice";
import { ADMIN_TEXTS } from "constants/adminTexts";
import "./ContactUsLocations.scss";

const T = ADMIN_TEXTS.contactUs;

const buildIframe = (input) => {
  const trimmed = input.trim();
  if (!trimmed) return "";

  if (trimmed.includes("<iframe")) return trimmed;

  if (trimmed.includes("google.com/maps/embed")) {
    return `<iframe src="${trimmed}" width="100%" height="300" frameborder="0" allowfullscreen loading="lazy" referrerpolicy="no-referrer-when-downgrade"></iframe>`;
  }

  const coordMatch = trimmed.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
  if (coordMatch) {
    return `<iframe src="https://maps.google.com/maps?q=${coordMatch[1]},${coordMatch[2]}&output=embed" width="100%" height="300" frameborder="0" allowfullscreen loading="lazy" referrerpolicy="no-referrer-when-downgrade"></iframe>`;
  }

  try {
    const url = new URL(trimmed);
    if (
      url.hostname.includes("google.com") ||
      url.hostname.includes("maps.app")
    ) {
      const q = url.searchParams.get("q");
      if (q) {
        return `<iframe src="https://maps.google.com/maps?q=${encodeURIComponent(q)}&output=embed" width="100%" height="300" frameborder="0" allowfullscreen loading="lazy" referrerpolicy="no-referrer-when-downgrade"></iframe>`;
      }
    }
  } catch {}

  return `<iframe src="https://maps.google.com/maps?q=${encodeURIComponent(trimmed)}&output=embed" width="100%" height="300" frameborder="0" allowfullscreen loading="lazy" referrerpolicy="no-referrer-when-downgrade"></iframe>`;
};

const validationSchema = Yup.object({
  address: Yup.string().required(T.addressRequired),
  mapSource: Yup.string().required(T.mapSourceRequired),
});

export const ContactUsLocations = () => {
  const dispatch = useDispatch();
  const { settings, loading, updateLoading, error, settingsExist } = useSelector(
    (state) => state.contactSettings,
  );

  const [mapPreviewHtml, setMapPreviewHtml] = useState("");

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      address: settings?.address || "",
      mapSource: settings?.mapIframe || "",
    },
    validationSchema,
    onSubmit: (values) => {
      dispatch(
        updateContactSettings({
          address: values.address,
          mapIframe: buildIframe(values.mapSource),
        }),
      );
    },
  });

  const handlePreviewMap = () => {
    formik.setFieldTouched("mapSource", true);
    const iframe = buildIframe(formik.values.mapSource);
    if (iframe) {
      setMapPreviewHtml(iframe);
    }
  };

  useEffect(() => {
    dispatch(getContactSettings());
    return () => {
      dispatch(clearError());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (settings?.mapIframe) {
      setMapPreviewHtml(settings.mapIframe);
    }
  }, [settings?.mapIframe]);

  if (loading) {
    return (
      <Box className="contact-us-locations__loading">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="contact-us-locations">
      <ComponentHeader title={T.locationsPageTitle} />
      {!settingsExist && (
        <Alert severity="info" className="contact-us-locations__alert">
          {T.settingsNotCreatedYet}
        </Alert>
      )}
      {error && (
        <Alert
          severity="error"
          className="contact-us-locations__alert"
          onClose={() => dispatch(clearError())}
        >
          {typeof error === "string" ? error : T.genericError}
        </Alert>
      )}
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={T.addressLabel}
              name="address"
              value={formik.values.address}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.address && Boolean(formik.errors.address)}
              helperText={formik.touched.address && formik.errors.address}
            />

            <Box className="contact-us-locations__map-source-box">
              <TextField
                fullWidth
                multiline
                rows={5}
                label={T.mapSourceLabel}
                name="mapSource"
                value={formik.values.mapSource}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.mapSource && Boolean(formik.errors.mapSource)
                }
                helperText={
                  (formik.touched.mapSource && formik.errors.mapSource) ||
                  T.mapSourceHelperText
                }
                inputProps={{
                  className: "contact-us-locations__map-source-input",
                }}
              />
            </Box>

            <Box className="contact-us-locations__actions">
              <BrandButton
                variant="secondary"
                startIcon={<Map />}
                onClick={handlePreviewMap}
                type="button"
              >
                {T.previewMapBtn}
              </BrandButton>
              <BrandButton
                type="submit"
                variant="primary"
                startIcon={<Save />}
                disabled={updateLoading || !settingsExist}
              >
                {updateLoading ? T.saving : T.save}
              </BrandButton>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography
              variant="subtitle2"
              className="contact-us-locations__map-label"
            >
              {T.mapPreviewLabel}
            </Typography>
            {mapPreviewHtml ? (
              <Paper
                variant="outlined"
                className="contact-us-locations__map-preview"
                dangerouslySetInnerHTML={{ __html: mapPreviewHtml }}
              />
            ) : (
              <Paper
                variant="outlined"
                className="contact-us-locations__map-empty"
              >
                <Typography variant="body2" color="text.disabled">
                  {T.mapPreviewEmpty}
                </Typography>
              </Paper>
            )}
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};
